// ============================================================================
// STEP 2: REBALANCE SCHEDULER
// ============================================================================

export type Frequency = 'D' | 'W' | 'M' | 'Q' | 'SA' | 'A'; // Daily, Weekly, Monthly, Quarterly, Semi-Annual, Annual

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  D: 'Daily',
  W: 'Weekly',
  M: 'Monthly',
  Q: 'Quarterly',
  SA: 'Semi Annually',
  A: 'Annually',
};
export type Weekday = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI';

// Mirror of the backend quant_toolbox scheduler_type.py enums — the step's
// output is this shape 1:1 (no adapter). See RebalanceSchedulerConfig.
export type RecTiming = 'MONTH_START' | 'MONTH_END' | 'CUSTOM_INTRA_MONTH';
export type RefDate =
  | 'PREV_BUSINESS_DAY'
  | 'CUSTOM_LAG'
  | 'SAME_DAY'
  | 'PREV_MONTH_END'
  | 'TWO_MONTHS_PRIOR'
  | 'THREE_MONTHS_PRIOR'
  | 'PREV_QUARTER_END'
  | 'PREV_YEAR_END';
export type WeekOfMonth = 1 | 2 | 3 | 4 | 'last';
export type RebalanceMethod = 'calendar_based';
export type TargetWeightMode = 'use_existing' | 'recompute';

export const REF_DATE_LABELS: Record<RefDate, string> = {
  PREV_BUSINESS_DAY: 'Previous Business Day (T-1)',
  CUSTOM_LAG: 'Custom Business Day Lag',
  SAME_DAY: 'Same Day (T)',
  PREV_MONTH_END: 'Previous Month End',
  TWO_MONTHS_PRIOR: 'Month End, 2 Months Prior',
  THREE_MONTHS_PRIOR: 'Month End, 3 Months Prior',
  PREV_QUARTER_END: 'Previous Quarter End',
  PREV_YEAR_END: 'Previous Year End',
};

export const TIMING_LABELS: Record<RecTiming, string> = {
  CUSTOM_INTRA_MONTH: 'Custom Intra-Month',
  MONTH_END: 'Month End',
  MONTH_START: 'Month Start',
};

// recon_scheduler — frequency + ref_date are required (backend ReconScheduler).
export interface ReconScheduler {
  frequency: Frequency;
  timing: RecTiming | null;
  first_month: number | null;
  week_of_month: WeekOfMonth | null;
  weekday: Weekday | null;
  ref_date: RefDate;
  custom_lag_days: number | null;
}

// rebal_scheduler — backend CalendarRebalancer. Kept as a full object in the
// store even when is_rebal_activated is false (so toggling doesn't lose the
// user's settings); the backend normalizes it to null when deactivated.
export interface RebalScheduler {
  rebal_methodology: RebalanceMethod;
  // Nullable for exactly ONE state: pre-2020 + Monthly reconstitution leaves
  // no strictly-finer legal frequency (D/W are banned), so the select is
  // legitimately empty and hasRebalanceFrequencyError explains why. Every
  // other schedule field always holds a concrete value (see the fill
  // contract in utils/normalizeScheduler.ts).
  frequency: Frequency | null;
  timing: RecTiming | null;
  first_month: number | null;
  week_of_month: WeekOfMonth | null;
  weekday: Weekday | null;
  ref_date: RefDate | null;
  custom_lag_days: number | null;
  rebal_target: TargetWeightMode;
}

// Universe-specific preset (the "Match to Universe" default schedule).
export interface RebalancerPreset {
  recon_scheduler: ReconScheduler;
  is_rebal_activated: boolean;
  rebal_scheduler: RebalScheduler;
}

// Step 2 output — emitted as the backtest input's `scheduler` dict 1:1.
// (start_date/end_date live top-level in BacktestConfig; kept here so the
// step owns the date range, sliced out at submission.)
export interface RebalanceScheduler {
  start_date: string; // ISO date string "YYYY-MM-DD"
  end_date: string;
  recon_scheduler: ReconScheduler;
  is_rebal_activated: boolean;
  rebal_scheduler: RebalScheduler;
}

// ============================================================================
// EVENT TIMELINE PREVIEW
// ============================================================================

export type EventType = 'reconstitution' | 'rebalance';

export interface TimelineEvent {
  date: string; // ISO date "YYYY-MM-DD"
  type: EventType;
  description: string;
}

export interface EventTableRow {
  effectiveDate: string; // ISO date
  type: EventType;
  constituents: number; // total universe size at the event (Figma column)
  assetsAdded: number;
  assetsDropped: number;
  turnoverEstimate: number; // percentage as decimal, e.g. 0.08 = 8%
}

export interface EventTimelineResponse {
  reconstitutionEvents: Array<TimelineEvent>;
  rebalanceEvents: Array<TimelineEvent>;
  tableData: Array<EventTableRow>;
}

export type AggregationLevel =
  | 'none'
  | 'monthly'
  | 'quarterly'
  | 'semi-annual'
  | 'annual';

export interface MonthBreakdown {
  month: string; // Short month name, e.g. "Jan"
  reconCount: number;
  rebalanceCount: number;
}

export interface EventCluster {
  date: string; // ISO date for individual, period key for aggregated
  label: string; // Display label (date or period name)
  position: number; // 0-1, proportional placement on the timeline
  events: Array<TimelineEvent>;
  aggregation: AggregationLevel;
  monthBreakdown: Array<MonthBreakdown>; // populated for quarterly/semi-annual/annual
}

export type EventFilterTab = 'all' | 'reconstitution' | 'rebalance';
